/* ============================================================
 * Tuff Tattoos — Measurement Event Layer  (tuff-events.js)
 * Phase 2 · v1.4
 * ------------------------------------------------------------
 * v1.4 (2026-10-28, conv-r11, Nic Round 9):
 *  - KTF's request form gets a Redirect URL setting pointing at
 *    /pages/thank-you (Nic sets it in KTF), so EVERY submission
 *    lands on thank-you, not only people who tap "Continue".
 *    Once that redirect is set, booking_confirmation_viewed is
 *    INTENDED AS THE CONVERSION (name, params and once-per-session
 *    guard unchanged, so GA4 history stays continuous).
 *  - NEW Meta Pixel standard event fbq('track','Lead') on
 *    /pages/thank-you, once per session under the same tuff_bcv
 *    guard. Sent only if window.fbq appears within ~10 s (same
 *    wait as the GA4 send). No parameters, no PII.
 *  - Funnel-return guard: on /pages/thank-you (or any page whose
 *    referrer is keepthefees.com) the URL's query is ignored, so
 *    whatever KTF's redirect appends (request id, utm_*, v...)
 *    can neither replace the _tuff_click set nor create the
 *    _tuff_attr first touch. Only ad keys on a normal landing
 *    page replace the set, as before.
 *  - Every other event unchanged.
 * v1.3 (2026-10-17, book link-out, Nic):
 *  - The KTF iframe is gone. /pages/book now links to the KTF
 *    request form full-page (same tab), so KTF's own GA4 tag
 *    (same property G-GKNKZ3M66P) sees the visit.
 *  - NEW click-id store: gclid, gbraid, wbraid, fbclid, utm_*
 *    and v are saved from the landing URL (sessionStorage +
 *    first-party cookie _tuff_click, 90 days). A URL that carries
 *    any of them replaces the stored set as a whole (last click
 *    wins); a URL with none leaves it alone.
 *  - Every link to ktf.keepthefees.com/form/request/135 gets the
 *    stored params appended (existing query + hash preserved).
 *  - NEW book_start_clicked (click on a KTF form link, beacon).
 *  - booking_form_view (iframe 50% visible) -> book_start_view
 *    (Start button 50% visible on /pages/book).
 *  - booking_form_engaged (iframe focus proxy) removed: there is
 *    no iframe; book_start_clicked is the real intent signal.
 *  - _tuff_attr first-touch cookie: unchanged.
 * v1.2 (2026-10-17, Track 1 Q20):
 *  - NEW booking_form_view: KTF iframe at least 50% visible on
 *    /pages/book (IntersectionObserver, once per page view).
 *  - booking_confirmation_viewed now also carries `artist`
 *    (from the existing _tuff_bep entry-point cookie).
 *  - Roster: + 'nick' (Nick's page is /pages/nick).
 *  - Existing click events kept under their current names
 *    (book_cta_clicked = CTA click, phone_clicked = phone tap)
 *    so GA4 history is not split.
 * v1.1 (2026-09-23):
 *  - FIX: init-time events (artist_page_viewed,
 *    booking_confirmation_viewed) were pushed into dataLayer
 *    before the Google tag's config commands and were dropped
 *    (gtag discards events queued ahead of any config). They now
 *    wait for the tag to initialize (window.google_tag_manager),
 *    retrying for up to 10s, then send regardless.
 *  - Click IDs: wbraid/gbraid now also map src -> 'google'
 *    (previously only gclid did).
 *  - Roster: + terrero, tiko.
 * Design rules:
 *  - No PII is ever read or sent (no names/emails/phones/free text).
 *  - Every event answers a business question; params over event sprawl.
 *  - Defensive: every handler wrapped; failure = silence, never breakage.
 * ============================================================ */
(function () {
  'use strict';
  if (window.__tuffEvents) return; window.__tuffEvents = 1;

  /* gtag shim: if the Google tag hasn't defined gtag yet (script order
     varies), queue into dataLayer directly — GA4 replays it on config. */
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function(){ window.dataLayer.push(arguments); };
  }

  /* ---------- config ---------------------------------------- */
  /* RESERVED NAME — do not use for anything else:
     'booking_request_submitted' is reserved for a deterministic TRUE
     KTF request-creation signal (candidate: KTF's native GA integration,
     pending the controlled test). The thank-you page does NOT qualify:
     it fires 'booking_confirmation_viewed' below. v1.4: with KTF's
     Redirect URL set to /pages/thank-you, that event is the intended
     conversion (every submission lands there); the reserved name
     stays reserved for a server-side KTF signal, if one ever exists. */
  var ADS_SEND_TO = '';            /* intentionally unused for the confirmation
                                      event; will belong to the future TRUE
                                      booking_request_submitted only */
  var BREAKOUT_ON_THANKYOU = true; /* framed thank-you promotes itself to the top
                                      window (same-origin nav) — fixes UX + keeps
                                      the session's original source intact */
  var ARTISTS = { ally:'Ally', tiago:'Tiago', dido:'Dido', brandon:'Brandon',
                  carlos:'Carlos', 'nick-1':'Nick', nick:'Nick', kim:'Kim',
                  terrero:'Terrero', tiko:'Tiko' };
  var SERVICE_PAGES = ['custom-tattoos','realism-color','fine-line-micro','floral-mandala',
    'black-grey-blackwork','cover-ups','large-scale-sleeves','guest-artists','services'];

  /* ---------- helpers --------------------------------------- */
  function safe(fn){ return function(){ try { return fn.apply(this, arguments); } catch(e){} }; }
  function send(name, params){
    try {
      params = params || {};
      params.page_type = params.page_type || pageType();
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      if (typeof window.fbq === 'function' && params.__fb) {
        window.fbq(params.__fb === 'Lead' ? 'track' : 'trackCustom',
                   params.__fb === 'Lead' ? 'Lead' : name, {});
      }
      delete params.__fb;
    } catch(e){}
  }
  /* v1.1: for events fired at script-init time. gtag drops events queued
     before any config command, so wait until the Google tag has
     initialized (config processed) before sending. Falls back to a
     plain send after ~10s so no event is ever lost silently. */
  function sendWhenReady(name, params, tries){
    tries = tries || 0;
    if (window.google_tag_manager || tries > 40) { send(name, params); return; }
    setTimeout(function(){ sendWhenReady(name, params, tries + 1); }, 250);
  }
  function handle(){ var m = location.pathname.match(/^\/pages\/([a-z0-9-]+)/); return m ? m[1] : null; }
  function artistFromPath(){ var h = handle(); return h && ARTISTS[h] ? ARTISTS[h] : null; }
  function pageType(){
    var p = location.pathname, h = handle();
    if (p === '/' ) return 'home';
    if (h && ARTISTS[h]) return 'artist';
    if (h === 'book') return 'book';
    if (h === 'thank-you') return 'thank_you';
    if (h === 'gallery') return 'gallery';
    if (h && SERVICE_PAGES.indexOf(h) > -1) return 'service';
    if (p.indexOf('/blogs/') === 0) return 'blog';
    return h ? 'page' : 'other';
  }
  function sectionOf(el){
    var s = el.closest && el.closest('.shopify-section');
    if (!s) return 'other';
    var c = s.className || '';
    if (/header/.test(c)) return 'header';
    var inner = s.querySelector('[class*="hero"]') ? 'hero' : null;
    if (el.closest('.tuff-mobile-bar,[class*="mobile-bar"]')) return 'mobile_bar';
    if (el.closest('footer')) return 'footer';
    if (s.querySelector('.artist-profile,[class*="artist-profile"]')) return 'artist_profile';
    if (/cta/.test(c) || s.querySelector('[class*="cta-centered"]')) return 'cta_band';
    return inner || 'content';
  }

  /* ---------- first-touch attribution capture ---------------- */
  /* First-party, 180-day cookie. IDs only — no PII. Used later to
     stamp booking_confirmation_viewed and (pending KTF capability
     verification) to hand attribution into the booking record.   */
  var ATTR_COOKIE = '_tuff_attr';
  function readCookie(n){
    var m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : null;
  }
  /* v1.4 funnel-return guard: thank-you (KTF's Redirect URL target)
     and any page reached from keepthefees.com are returns from our own
     booking funnel, never an ad landing. Their query string (whatever
     KTF appends) is ignored for attribution storage.                 */
  var FUNNEL_RETURN = false;
  safe(function(){
    var rh = ''; try { rh = document.referrer ? new URL(document.referrer).hostname : ''; } catch(e){}
    FUNNEL_RETURN = pageType() === 'thank_you' || /(^|\.)keepthefees\.com$/.test(rh);
  })();
  var firstTouch = null;
  safe(function(){
    firstTouch = JSON.parse(readCookie(ATTR_COOKIE) || 'null');
    if (!firstTouch) {
      var q = new URLSearchParams(FUNNEL_RETURN ? '' : location.search);
      var ref = document.referrer || '';
      var refHost = ''; try { refHost = ref ? new URL(ref).hostname : ''; } catch(e){}
      var internal = /(^|\.)tufftattoos\.com$/.test(refHost) || /keepthefees\.com$/.test(refHost);
      var adClick = q.get('gclid') || q.get('wbraid') || q.get('gbraid');
      firstTouch = {
        src:  q.get('utm_source')   || (internal ? null : (refHost || null)) || (adClick ? 'google' : null) || 'direct',
        med:  q.get('utm_medium')   || (adClick ? 'cpc'
              : (refHost && !internal ? 'referral' : 'none')),
        cmp:  q.get('utm_campaign') || null,
        cnt:  q.get('utm_content')  || null,
        gclid:  q.get('gclid')  || null,
        wbraid: q.get('wbraid') || null,
        gbraid: q.get('gbraid') || null,
        fbclid: q.get('fbclid') || null,
        lp: location.pathname.slice(0, 120),
        ts: Date.now()
      };
      if (FUNNEL_RETURN) return; /* v1.4: label this view, never persist a funnel return as first touch */
      var v = encodeURIComponent(JSON.stringify(firstTouch));
      document.cookie = ATTR_COOKIE + '=' + v + ';path=/;max-age=' + (180*86400) + ';domain=.tufftattoos.com;SameSite=Lax;Secure';
    }
  })();
  function attrParams(){
    if (!firstTouch) return {};
    return { ft_source: firstTouch.src, ft_medium: firstTouch.med,
             ft_campaign: firstTouch.cmp || '(none)', ft_landing: firstTouch.lp };
  }

  /* ---------- click-id store + KTF link decoration (v1.3) ------ */
  /* Separate from _tuff_attr (first touch, 180 days, never
     overwritten): this is the LATEST ad click / campaign tag set,
     handed to the KTF form so KTF's GA4 tag credits the request
     to the right ad. IDs and campaign tags only, no PII.          */
  var CLICK_COOKIE = '_tuff_click', CLICK_SS = 'tuff_click';
  var KTF_FORM_RE = /^https?:\/\/ktf\.keepthefees\.com\/form\/request\/135(?:[/?#]|$)/i;
  var CLICK_KEYS = ['gclid','gbraid','wbraid','fbclid','utm_source','utm_medium',
                    'utm_campaign','utm_term','utm_content','utm_id','v'];
  function clickPairsFromUrl(search){
    var q = new URLSearchParams(search || ''), out = [], seen = {};
    q.forEach(function(val, key){
      var k = key.toLowerCase();
      if (CLICK_KEYS.indexOf(k) > -1 && !seen[k] && val !== '') {
        seen[k] = 1; out.push([k, String(val).slice(0, 200)]);
      }
    });
    return out;
  }
  function pairsToQuery(pairs){
    return pairs.map(function(p){ return encodeURIComponent(p[0]) + '=' + encodeURIComponent(p[1]); }).join('&');
  }
  function writeClickSet(qs){
    try { sessionStorage.setItem(CLICK_SS, qs); } catch(e){}
    try {
      var dom = /(^|\.)tufftattoos\.com$/.test(location.hostname) ? ';domain=.tufftattoos.com' : '';
      document.cookie = CLICK_COOKIE + '=' + encodeURIComponent(qs) + ';path=/;max-age=' + (90*86400) + dom + ';SameSite=Lax;Secure';
    } catch(e){}
  }
  function readClickSet(){
    var qs = null;
    try { qs = sessionStorage.getItem(CLICK_SS); } catch(e){}
    if (!qs) { qs = readCookie(CLICK_COOKIE); if (qs) { try { sessionStorage.setItem(CLICK_SS, qs); } catch(e){} } }
    return qs ? clickPairsFromUrl('?' + qs) : [];
  }
  safe(function(){
    if (FUNNEL_RETURN) return; /* v1.4: KTF redirect params never replace the set */
    var pairs = clickPairsFromUrl(location.search);
    if (pairs.length) writeClickSet(pairsToQuery(pairs)); /* last click wins, whole set */
  })();
  function decorateKtf(a){
    var href = a.getAttribute('href') || '';
    if (!KTF_FORM_RE.test(href)) return;
    var pairs = readClickSet();
    if (!pairs.length) return;
    var hash = '', i = href.indexOf('#');
    if (i > -1) { hash = href.slice(i); href = href.slice(0, i); }
    var qi = href.indexOf('?');
    var existing = new URLSearchParams(qi > -1 ? href.slice(qi + 1) : '');
    var add = pairs.filter(function(p){ return !existing.has(p[0]); });
    if (!add.length) return;
    var sep = qi === -1 ? '?' : (/[?&]$/.test(href) ? '' : '&');
    a.setAttribute('href', href + sep + pairsToQuery(add) + hash);
  }
  function decorateAllKtf(){
    var links = document.querySelectorAll('a[href*="keepthefees.com"]');
    for (var j = 0; j < links.length; j++) safe(decorateKtf)(links[j]);
  }
  safe(decorateAllKtf)();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', safe(decorateAllKtf));
  /* bfcache restore (Back from KTF): stored set may have changed in another tab */
  window.addEventListener('pageshow', safe(function(e){ if (e.persisted) decorateAllKtf(); }));

  /* ---------- thank-you: booking_confirmation_viewed + Meta Lead -- */
  /* History: proven 2026-09-22 that this page was reached only by
     users who tapped KTF's optional post-submit Continue (~50/mo of
     ~245 true requests when the form was embedded), so it was a
     diagnostic event only.
     v1.4: KTF's request form Redirect URL is set (by Nic, in KTF) to
     /pages/thank-you, so every submission lands here. From then on
     booking_confirmation_viewed is INTENDED AS THE CONVERSION
     (mark it as a GA4 key event / import to Ads in the admin UIs,
     not in code), and the same view sends Meta's standard Lead.
     Both share one once-per-session guard (sessionStorage tuff_bcv),
     so reloads never double count. Before the redirect is live the
     counts still under-report. The KTF request list stays the
     authoritative request count.                                   */
  /* Meta Lead: wait for the Meta Pixel (window.fbq) like the GA4
     wait (250 ms x 40 = ~10 s). If it never appears, send nothing.
     No parameters at all, so no PII can be passed.                 */
  function fbLeadWhenReady(tries){
    tries = tries || 0;
    if (typeof window.fbq === 'function') { try { window.fbq('track', 'Lead'); } catch(e){} return; }
    if (tries >= 40) return;
    setTimeout(function(){ fbLeadWhenReady(tries + 1); }, 250);
  }
  if (pageType() === 'thank_you') {
    var FRAMED = (function(){ try { return window.self !== window.top; } catch(e){ return true; } })();
    if (FRAMED && BREAKOUT_ON_THANKYOU) {
      /* Defensive only — frame-ancestors currently prevents framed
         loads entirely; harmless no-op unless that ever changes.    */
      safe(function(){ window.top.location.replace(location.href); })();
    }
    safe(function(){
      if (FRAMED && BREAKOUT_ON_THANKYOU) return;
      if (sessionStorage.getItem('tuff_bcv')) return;
      sessionStorage.setItem('tuff_bcv','1');
      var p = attrParams();
      p.booking_entry_point = readCookie('_tuff_bep') || 'unknown';
      var bepArtist = /^artist:([^|]+)/.exec(p.booking_entry_point);
      p.artist = bepArtist ? bepArtist[1] : '(none)';
      p.page_type = 'thank_you';
      sendWhenReady('booking_confirmation_viewed', p);
      fbLeadWhenReady(0); /* v1.4: same guard, once per session */
    })();
  }

  /* ---------- artist page view -------------------------------- */
  safe(function(){
    var a = artistFromPath();
    if (a) sendWhenReady('artist_page_viewed', { artist: a, page_type: 'artist' });
  })();

  /* ---------- click delegation -------------------------------- */
  document.addEventListener('click', safe(function(ev){
    var el = ev.target && ev.target.closest ? ev.target.closest('a,button') : null;
    if (!el) return;
    var href = el.getAttribute && (el.getAttribute('href') || '');

    /* v1.3: Start-your-request link to the KTF form (same tab).
       Re-decorate right before navigation, then fire a beacon event
       that never blocks or delays the navigation.                 */
    if (href && KTF_FORM_RE.test(href)) {
      decorateKtf(el);
      var hasClick = readClickSet().some(function(p){ return /^(gclid|gbraid|wbraid|fbclid)$/.test(p[0]); });
      var bepK = readCookie('_tuff_bep') || ('page:' + pageType() + '|' + sectionOf(el));
      send('book_start_clicked', { cta_location: sectionOf(el), booking_entry_point: bepK,
        has_click_id: hasClick ? 'yes' : 'no', transport_type: 'beacon' });
      return;
    }
    /* Book CTA — any link into /pages/book */
    if (href && href.indexOf('/pages/book') > -1) {
      var a = artistFromPath();
      var loc = sectionOf(el);
      var bep = (a ? 'artist:' + a : 'page:' + pageType()) + '|' + loc;
      document.cookie = '_tuff_bep=' + encodeURIComponent(bep) + ';path=/;max-age=7200;SameSite=Lax;Secure';
      send('book_cta_clicked', { cta_location: loc, artist: a || '(none)',
        cta_text: (el.textContent||'').trim().slice(0,40) });
      return;
    }
    if (href && href.indexOf('tel:') === 0)   { send('phone_clicked',  { link_location: sectionOf(el) }); return; }
    if (href && href.indexOf('mailto:') === 0){ send('email_clicked',  { link_location: sectionOf(el) }); return; }
    if (href && /maps\.google|google\.[a-z.]+\/maps|maps\.apple/.test(href)) {
      send('directions_clicked', { link_location: sectionOf(el) }); return;
    }
    if (href && /instagram\.com/.test(href)) {
      send('instagram_clicked', { artist: artistFromPath() || '(studio)', link_location: sectionOf(el) }); return;
    }
    /* Portfolio engagement: gallery filter chips + gallery/work images */
    var fbtn = ev.target.closest && ev.target.closest('[data-filter]');
    if (fbtn) { send('artist_portfolio_engaged', { engagement_type:'filter',
      filter_value:(fbtn.getAttribute('data-filter')||'').slice(0,40) }); return; }
    var piece = ev.target.closest && ev.target.closest('[data-tags]');
    if (piece) {
      var tags = (piece.getAttribute('data-tags')||'').split('|');
      send('artist_portfolio_engaged', { engagement_type:'piece',
        artist: tags[0] ? tags[0].charAt(0).toUpperCase()+tags[0].slice(1) : '(unknown)' });
      return;
    }
  }), { capture: true, passive: true });

  /* ---------- contact form ----------------------------------- */
  safe(function(){
    var form = document.querySelector('form[action*="/contact"]');
    if (!form) return;
    var started = false;
    form.addEventListener('focusin', safe(function(){
      if (started) return; started = true;
      send('contact_form_started', {});
    }));
    form.addEventListener('submit', safe(function(){
      send('contact_form_submitted', { note: 'submitted_not_confirmed' });
    }));
  })();

  /* ---------- book start view (v1.3, replaces booking_form_view) */
  /* Denominator for the link-out: the "Start your request" button
     was at least half visible once on this /pages/book view.       */
  safe(function(){
    if (pageType() !== 'book' || !('IntersectionObserver' in window)) return;
    var btn = document.querySelector('a[data-tuff-start]');
    if (!btn) return;
    var seen = false;
    var io = new IntersectionObserver(safe(function(entries){
      for (var i = 0; i < entries.length; i++) {
        if (!seen && entries[i].isIntersecting && entries[i].intersectionRatio >= 0.5) {
          seen = true; io.disconnect();
          send('book_start_view', { device: window.matchMedia('(max-width: 1023.98px)').matches ? 'mobile' : 'desktop' });
        }
      }
    }), { threshold: [0.5] });
    io.observe(btn);
  })();
})();
